import { useState, useEffect, useCallback, Fragment } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { RefreshCw, ChevronRight, ChevronDown, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { compensationAPI } from '../../lib/api';
import { fmt, statusBadge } from './_shared';

function RecordExpand({ record }) {
  const payouts = record.payouts || [];
  const hasManagerPayout = payouts.some((p) => p.payee_type === 'Manager');
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm">
        <div className="font-medium text-zinc-700">Payouts ({payouts.length})</div>
        <div className="flex items-center gap-4 text-xs text-zinc-500">
          <span>Agency: <span className="font-medium text-zinc-700">{record.agency_name || record.agency_id || '—'}</span></span>
          <span data-testid={`record-manager-${record.record_id}`}>
            Manager: {record.manager_id ? (
              <span className="font-medium text-zinc-700">{record.manager_name || record.manager_id}</span>
            ) : (
              <span className="italic">None — no payout generated</span>
            )}
          </span>
        </div>
      </div>
      {payouts.length === 0 ? (
        <div className="text-sm text-zinc-500 italic">No matching rules — record has no payouts.</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Payee Type</TableHead>
              <TableHead>Payee</TableHead>
              <TableHead>Rule</TableHead>
              <TableHead>Basis</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Explanation</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payouts.map((p, i) => (
              <TableRow key={i} data-testid={`comp-payout-${p.payee_type}`}>
                <TableCell><Badge variant="outline">{p.payee_type}</Badge></TableCell>
                <TableCell className="text-sm">{p.payee_name || p.payee_id || '—'}</TableCell>
                <TableCell className="text-sm">{p.rule_name}</TableCell>
                <TableCell className="text-sm">{p.calculation_basis}</TableCell>
                <TableCell className="text-right font-medium">{fmt(p.amount)}</TableCell>
                <TableCell className="text-xs text-zinc-600">{p.explanation}{p.needs_review && <Badge className="ml-1 bg-amber-100 text-amber-800">Review</Badge>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {!hasManagerPayout && record.manager_id && (
        <div className="text-xs text-zinc-500 italic">Manager {record.manager_name || record.manager_id} is assigned but no Manager rule matched this record.</div>
      )}
    </div>
  );
}

export function RecordsTab({ statementMonth }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState({ status: '', needs_review: '' });
  const [deletingId, setDeletingId] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statementMonth) params.statement_month = statementMonth;
      if (filters.status) params.status = filters.status;
      if (filters.needs_review !== '') params.needs_review = filters.needs_review;
      const r = await compensationAPI.listRecords(params);
      setRecords(r.data.records || []);
    } catch {
      toast.error('Failed to load records');
    } finally { setLoading(false); }
  }, [statementMonth, filters]);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async (e, r) => {
    e.stopPropagation();
    if (r.status === 'Paid') {
      toast.error('Paid records cannot be deleted — create an adjustment instead');
      return;
    }
    if (!window.confirm(
      `Delete this record?\n\n${r.policy_holder} · ${r.carrier_name || r.carrier_id} · ${r.statement_month}\n\nThis cannot be undone.`
    )) return;
    setDeletingId(r.record_id);
    try {
      await compensationAPI.deleteRecord(r.record_id);
      toast.success('Record deleted');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={filters.status || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-48" data-testid="comp-records-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {['Imported', 'Pending_Review', 'Accounting_Reviewed', 'Approved', 'Scheduled', 'Paid', 'Voided'].map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.needs_review === '' ? 'all' : String(filters.needs_review)}
                onValueChange={(v) => setFilters((f) => ({ ...f, needs_review: v === 'all' ? '' : v === 'true' }))}>
          <SelectTrigger className="w-44" data-testid="comp-records-review-filter"><SelectValue placeholder="Needs Review" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any</SelectItem>
            <SelectItem value="true">Needs Review</SelectItem>
            <SelectItem value="false">Clean</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="comp-records-refresh">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
        <span className="text-sm text-zinc-500 ml-auto">{records.length} records</span>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead>Month</TableHead>
              <TableHead>Policy Holder</TableHead>
              <TableHead>Carrier</TableHead>
              <TableHead>Agent NPN</TableHead>
              <TableHead className="text-right">Expected</TableHead>
              <TableHead className="text-right">Actual</TableHead>
              <TableHead className="text-right">Ratio</TableHead>
              <TableHead className="text-right">Final</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.length === 0 && !loading && (
              <TableRow><TableCell colSpan={11} className="text-center text-zinc-500 py-8">No records found</TableCell></TableRow>
            )}
            {records.map((r) => {
              const isOpen = expandedId === r.record_id;
              return (
                <Fragment key={r.record_id}>
                  <TableRow className="cursor-pointer hover:bg-zinc-50"
                            onClick={() => setExpandedId(isOpen ? null : r.record_id)}
                            data-testid={`comp-record-row-${r.record_id}`}>
                    <TableCell>{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
                    <TableCell>{r.statement_month}</TableCell>
                    <TableCell className="font-medium">{r.policy_holder}</TableCell>
                    <TableCell className="text-sm">{r.carrier_name || r.carrier_id}</TableCell>
                    <TableCell className="text-sm">{r.writing_agent_npn || '—'}</TableCell>
                    <TableCell className="text-right">{fmt(r.expected_amount)}</TableCell>
                    <TableCell className="text-right">{fmt(r.actual_carrier_paid)}</TableCell>
                    <TableCell className="text-right">{r.proration_ratio !== null && r.proration_ratio !== undefined ? r.proration_ratio.toFixed(3) : '—'}</TableCell>
                    <TableCell className="text-right font-semibold">{fmt(r.final_compensation)}</TableCell>
                    <TableCell>{statusBadge(r.status)}{r.needs_review && <Badge className="ml-1 bg-amber-100 text-amber-800">Review</Badge>}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleDelete(e, r)}
                        disabled={deletingId === r.record_id || r.status === 'Paid'}
                        data-testid={`comp-record-delete-${r.record_id}`}
                        title={r.status === 'Paid' ? 'Paid records cannot be deleted' : 'Delete this record'}
                      >
                        {deletingId === r.record_id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <Trash2 className={`w-4 h-4 ${r.status === 'Paid' ? 'text-zinc-300' : 'text-rose-600'}`} />
                        }
                      </Button>
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow className="bg-zinc-50/50">
                      <TableCell colSpan={11} className="p-4">
                        <RecordExpand record={r} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
